import React from 'react';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  TrashIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

export type ConfirmVariant = 'danger' | 'warning' | 'info' | 'success';

interface ConfirmActionModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  variant?: ConfirmVariant;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const VARIANT_STYLES: Record<ConfirmVariant, { button: string; iconBg: string; icon: string }> = {
  danger: {
    button: 'bg-red-600 hover:bg-red-700 text-white',
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    icon: 'text-red-600 dark:text-red-400',
  },
  warning: {
    button: 'bg-amber-600 hover:bg-amber-700 text-white',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  info: {
    button: 'bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast',
    iconBg: 'bg-lex-accent-soft dark:bg-lex-accent-soft',
    icon: 'text-lex-accent dark:text-lex-accent',
  },
  success: {
    button: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
};

const VARIANT_ICONS: Record<ConfirmVariant, React.FC<{ className?: string }>> = {
  danger: TrashIcon,
  warning: PaperAirplaneIcon,
  info: CheckCircleIcon,
  success: RocketLaunchIcon,
};

const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  title,
  message,
  confirmLabel,
  variant = 'warning',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const styles = VARIANT_STYLES[variant];
  const IconComponent = VARIANT_ICONS[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onCancel}
        style={{ animation: 'lex-overlay-in 150ms ease-out forwards' }}
      />

      <div
        className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ animation: 'lex-modal-in 220ms ease-out forwards' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${styles.iconBg}`}>
              {variant === 'danger' ? (
                <ExclamationTriangleIcon className={`h-5 w-5 ${styles.icon}`} />
              ) : (
                <IconComponent className={`h-5 w-5 ${styles.icon}`} />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${styles.button}`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Processing...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionModal;
